import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import * as Cookies from 'js-cookie';

/**
 * Hook to detect if the app is running in a mobile webview (iOS or Android)
 *
 * The hostApp value is determined by:
 * 1. Check URL query parameter: ?hostApp=ios or ?hostApp=android
 * 2. If found, persist to cookie for future visits
 * 3. Otherwise, read from existing cookie
 *
 * Example URLs:
 * - https://www.zillow.com/rental-manager/?hostApp=ios
 * - https://www.zillow.com/rental-manager/?hostApp=android
 *
 * @returns true if running in iOS or Android webview, false otherwise
 */
const HOST_APP_COOKIE_NAME = 'hostApp';
const IOS_VALUE = 'ios';
const ANDROID_VALUE = 'android';

export const useIsInMobileWebView = (): boolean => {
  const [isInMobileWebView, setIsInMobileWebView] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;

    const hostAppRaw = router.query[HOST_APP_COOKIE_NAME];
    const hostAppParam = Array.isArray(hostAppRaw) ? hostAppRaw[0] : (hostAppRaw ?? null);

    if (hostAppParam) {
      Cookies.set(HOST_APP_COOKIE_NAME, hostAppParam, { path: '/' });
      setIsInMobileWebView(hostAppParam === ANDROID_VALUE || hostAppParam === IOS_VALUE);
    } else {
      const hostAppCookie = Cookies.get(HOST_APP_COOKIE_NAME);
      setIsInMobileWebView(hostAppCookie === ANDROID_VALUE || hostAppCookie === IOS_VALUE);
    }
  }, [router.isReady, router.query]);

  return isInMobileWebView;
};
